import { useEffect, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FeedInput from "@/components/try-episode/FeedInput";
import EpisodeSelector from "@/components/try-episode/EpisodeSelector";
import TranscriptionProgress from "@/components/try-episode/TranscriptionProgress";
import SessionHeader from "@/components/try-episode/SessionHeader";
import OneOffChat from "@/components/try-episode/OneOffChat";
import { useOneOffSession } from "@/hooks/useOneOffSession";
import { usePodcasts } from "@/hooks/usePodcasts";
import { apiClient, type FeedPreview, type OneOffSession } from "@/api/client";
import { theme } from "@/lib/theme";
import { cn } from "@/lib/utils";

type PageState =
  | { type: "idle" }
  | { type: "selecting"; preview: FeedPreview; feedUrl?: string; podcastSlug?: string }
  | { type: "transcribing"; session: OneOffSession }
  | { type: "chatting"; session: OneOffSession }
  | { type: "expired" }
  | { type: "indexing"; podcastName: string };

export default function TryEpisodePage() {
  const { session: existingSession, isLoading: sessionLoading, refreshSession } = useOneOffSession();
  const { podcasts } = usePodcasts();
  const [pageState, setPageState] = useState<PageState>({ type: "idle" });
  const [feedError, setFeedError] = useState<string | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [processingPodcastIds, setProcessingPodcastIds] = useState<Set<string>>(new Set());

  // Sync with existing session on mount/update
  useEffect(() => {
    if (sessionLoading) return;

    if (existingSession) {
      if (existingSession.status === "expired") {
        setPageState({ type: "expired" });
      } else if (existingSession.status === "transcribing") {
        setPageState({ type: "transcribing", session: existingSession });
      } else if (
        existingSession.status === "ready" ||
        existingSession.status === "partial"
      ) {
        setPageState({ type: "chatting", session: existingSession });
      } else if (existingSession.status === "failed") {
        // All episodes failed - show error and allow retry
        setPageState({ type: "idle" });
        setFeedError("All episodes failed to transcribe. Please try different episodes.");
      }
    }
  }, [existingSession, sessionLoading]);

  // Update session state when polling returns new data
  useEffect(() => {
    if (!existingSession) return;

    if (pageState.type === "transcribing") {
      if (
        existingSession.status === "ready" ||
        existingSession.status === "partial"
      ) {
        setPageState({ type: "chatting", session: existingSession });
      } else if (existingSession.status === "failed") {
        setPageState({ type: "idle" });
        setFeedError("All episodes failed to transcribe. Please try again.");
      } else if (existingSession.status === "transcribing") {
        // Update with latest data
        setPageState({ type: "transcribing", session: existingSession });
      }
    } else if (pageState.type === "chatting") {
      // Keep session data fresh
      setPageState({ type: "chatting", session: existingSession });
    }
  }, [existingSession, pageState.type]);

  const handlePreviewFeed = async (feedUrl: string) => {
    setIsPreviewLoading(true);
    setFeedError(null);

    try {
      const preview = await apiClient.previewFeed({ feed_url: feedUrl });
      // If the feed was recognized, store the podcast_slug for instant session creation
      setPageState({
        type: "selecting",
        preview,
        feedUrl,
        podcastSlug: preview.podcast_slug,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load feed";
      setFeedError(message);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleSelectPodcast = async (podcastSlug: string) => {
    setIsPreviewLoading(true);
    setFeedError(null);

    try {
      const preview = await apiClient.previewFeed({ podcast_slug: podcastSlug });

      // Check if the podcast is still indexing (no episodes returned but podcast should have some)
      const podcast = podcasts.find(p => p.id === podcastSlug);
      const expectedEpisodeCount = podcast?.episodeCount ?? 0;

      if (preview.episodes.length === 0 && expectedEpisodeCount > 0) {
        // Podcast is still being indexed - add to processing set and show indexing state
        setProcessingPodcastIds(prev => {
          const next = new Set(prev);
          next.add(podcastSlug);
          return next;
        });
        setPageState({
          type: "indexing",
          podcastName: podcast?.name ?? "this podcast",
        });
        return;
      }

      setPageState({
        type: "selecting",
        preview,
        podcastSlug,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load podcast";
      setFeedError(message);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleStartSession = async (episodeIndices: number[], episodeNumbers: number[]) => {
    if (pageState.type !== "selecting") return;

    // Warn if there's an existing session
    if (existingSession) {
      const confirmed = window.confirm(
        "Starting a new session will end your current session. Continue?"
      );
      if (!confirmed) return;
    }

    setIsCreatingSession(true);
    setFeedError(null);

    try {
      // Check if all selected episodes have transcripts and we have a podcast_slug
      const selectedEpisodes = pageState.preview.episodes.filter((ep) =>
        episodeIndices.includes(ep.index)
      );
      const allHaveTranscripts = selectedEpisodes.every((ep) => ep.has_transcript === true);
      const canUseInstant =
        pageState.podcastSlug &&
        allHaveTranscripts &&
        episodeNumbers.length === selectedEpisodes.length;

      let session: OneOffSession;

      if (canUseInstant) {
        // Use podcast_slug mode for instant session
        session = await apiClient.createOneOffSession({
          podcast_slug: pageState.podcastSlug!,
          episode_numbers: episodeNumbers,
        });
      } else if (pageState.feedUrl) {
        // Use feed_url mode (may trigger transcription)
        session = await apiClient.createOneOffSession({
          feed_url: pageState.feedUrl,
          episode_indices: episodeIndices,
        });
      } else {
        throw new Error("No feed URL or podcast slug available");
      }

      // Skip transcribing state if session is already ready
      if (session.status === "ready" || session.status === "partial") {
        setPageState({ type: "chatting", session });
      } else {
        setPageState({ type: "transcribing", session });
      }
      refreshSession();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to create session";
      setFeedError(message);
    } finally {
      setIsCreatingSession(false);
    }
  };

  const handleChangeFeed = () => {
    setPageState({ type: "idle" });
    setFeedError(null);
  };

  const handleNewSession = () => {
    const confirmed = window.confirm(
      "Starting a new session will end your current session. Continue?"
    );
    if (!confirmed) return;

    setPageState({ type: "idle" });
    setFeedError(null);
  };

  // Initial loading state
  if (sessionLoading && pageState.type === "idle") {
    return (
      <div className={cn("min-h-screen", theme.colors.backgroundCream, "flex flex-col")}>
        <Header />
        <main className="flex-1 flex items-center justify-center px-4 py-16">
          <div className="flex flex-col items-center gap-3 text-gray-700 dark:text-gray-200">
            <div className="w-12 h-12 border-4 border-gray-200 dark:border-gray-700 border-t-transparent rounded-full animate-spin" />
            <p className="text-lg font-medium">Loading...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={cn("min-h-screen", theme.colors.backgroundCream, "flex flex-col")}>
      <Header />
      <main className="flex-1 px-4 py-8 max-w-5xl mx-auto w-full">
        {/* Idle state - show feed input */}
        {pageState.type === "idle" && (
          <FeedInput
            onSubmit={handlePreviewFeed}
            onSelectPodcast={handleSelectPodcast}
            isLoading={isPreviewLoading}
            error={feedError}
            processingPodcastIds={processingPodcastIds}
          />
        )}

        {/* Expired session state */}
        {pageState.type === "expired" && (
          <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
            <div className={cn(theme.cards.base, "p-8 md:p-12 max-w-2xl w-full text-center")}>
              <div className="text-6xl mb-4">⏰</div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3">
                Session Expired
              </h1>
              <p className="text-gray-600 dark:text-gray-300 mb-8">
                Your session has expired. Start a new session to continue chatting.
              </p>
              <button
                onClick={handleNewSession}
                className={cn(theme.buttons.primary, "px-6 py-3 rounded-xl text-lg")}
              >
                Start New Session
              </button>
            </div>
          </div>
        )}

        {/* Indexing state - podcast is still being indexed */}
        {pageState.type === "indexing" && (
          <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
            <div className={cn(theme.cards.base, "p-8 md:p-12 max-w-2xl w-full text-center")}>
              <div
                className={cn(
                  "mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-6",
                  "bg-amber-100 dark:bg-amber-900/30",
                  "border-3 border-amber-500 dark:border-amber-600",
                )}
              >
                <span className="w-10 h-10 border-4 border-amber-600 dark:border-amber-400 border-t-transparent rounded-full animate-spin" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3">
                Podcast Indexing
              </h1>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                We're still indexing <strong>{pageState.podcastName}</strong>. This can take up to 24 hours.
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
                You'll receive an email when it's ready.
              </p>
              <button
                onClick={handleChangeFeed}
                className={cn(theme.buttons.primary, "px-6 py-3 rounded-xl text-lg")}
              >
                Try Another Podcast
              </button>
            </div>
          </div>
        )}

        {/* Selecting episodes state */}
        {pageState.type === "selecting" && (
          <EpisodeSelector
            preview={pageState.preview}
            onStartSession={handleStartSession}
            onChangeFeed={handleChangeFeed}
            isLoading={isCreatingSession}
          />
        )}

        {/* Transcribing state */}
        {pageState.type === "transcribing" && (
          <div className={cn(theme.cards.base, "p-6 md:p-8 max-w-3xl mx-auto")}>
            <SessionHeader
              feedTitle={pageState.session.feed_title}
              expiresAt={pageState.session.expires_at}
            />
            <TranscriptionProgress episodes={pageState.session.episodes} />
          </div>
        )}

        {/* Chatting state */}
        {pageState.type === "chatting" && (
          <OneOffChat
            session={pageState.session}
            onNewSession={handleNewSession}
            onRefreshSession={refreshSession}
          />
        )}
      </main>

      <Footer />
    </div>
  );
}
